import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_SCREENSHOT_MAX_BYTES,
  FEEDBACK_SCREENSHOT_MIME_TYPES,
  FEEDBACK_SCREENSHOT_RULE_TEXT,
  FEEDBACK_SCREENSHOT_WARNING,
  GENERIC_FEEDBACK_QUESTION,
  MAX_FEEDBACK_MESSAGE_LENGTH,
  type FeedbackCategory,
  type FeedbackPageKey,
} from '@blueprint/domain'
import { useEffect, useRef, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button, Field, Select, TextArea } from '../../components/controls.js'
import { RepositoryError } from '../../data/index.js'
import { sessionRepository } from '../../state/session-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './FeedbackTab.module.css'

const CATEGORY_LABELS: Record<FeedbackCategory, string> = { FEEDBACK: 'Feedback', PROBLEM: 'Problem', QUESTION: 'Question' }

/** A picked file as the `data:…;base64,…` string the API takes; it is also the preview's source. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read that file.'))
    reader.onload = () => resolve(String(reader.result))
    reader.readAsDataURL(file)
  })
}

/**
 * The Feedback button and its popup (docs/feedback-mechanism-porting-guide.md sections 1
 * to 3). Rendered in the footer bar of each page a tenant user sees (the board, the boards
 * list, the mind map and the workflow), so it covers nothing and platform staff, who see none
 * of these pages, never get it; and only while Teamcentric Accounts is on: the question for each page comes from
 * Accounts, and each submission goes there. Closing the popup only closes it; there is no
 * "don't show again".
 */
export function FeedbackTab() {
  const view = useUiStore((state) => state.view)
  const usersOpen = useUiStore((state) => state.usersOpen)
  // The view the app shows: Storymap has no router, so this is the page.
  const pageKey: FeedbackPageKey = usersOpen ? '/users' : `/${view}`
  const [accounts, setAccounts] = useState(false)
  const [open, setOpen] = useState(false)
  const [thanks, setThanks] = useState(false)

  useEffect(() => {
    let cancelled = false
    sessionRepository().authConfig().then((config) => !cancelled && setAccounts(config.accounts === true), () => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!thanks) return
    const timer = setTimeout(() => setThanks(false), 3000)
    return () => clearTimeout(timer)
  }, [thanks])

  if (!accounts) return null
  return (
    <>
      <button
        type="button"
        className={styles.launcher}
        aria-haspopup="dialog"
        aria-label="Give feedback about this page"
        onClick={() => setOpen(true)}
      >
        Feedback
      </button>
      <FeedbackDialog
        open={open}
        pageKey={pageKey}
        onClose={() => setOpen(false)}
        onSent={() => {
          setOpen(false)
          setThanks(true)
        }}
      />
      {thanks ? (
        <div className={styles.toast} role="status">
          Thank you — your feedback was sent.
        </div>
      ) : null}
    </>
  )
}

function FeedbackDialog({ open, pageKey, onClose, onSent }: { open: boolean; pageKey: FeedbackPageKey; onClose: () => void; onSent: () => void }) {
  const [question, setQuestion] = useState(GENERIC_FEEDBACK_QUESTION)
  const [category, setCategory] = useState<'' | FeedbackCategory>('')
  const [message, setMessage] = useState('')
  const [touched, setTouched] = useState(false)
  const [screenshot, setScreenshot] = useState<{ name: string; dataUrl: string } | null>(null)
  const [screenshotError, setScreenshotError] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  const [sending, setSending] = useState(false)
  const [notSent, setNotSent] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // Asked each time the popup opens; the generic question stands if Accounts can't answer.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    sessionRepository().feedbackQuestion(pageKey).then((asked) => !cancelled && setQuestion(asked), () => {})
    return () => {
      cancelled = true
    }
  }, [open, pageKey])

  const reset = () => {
    setQuestion(GENERIC_FEEDBACK_QUESTION)
    setCategory('')
    setMessage('')
    setTouched(false)
    setScreenshot(null)
    setScreenshotError(null)
    setNotSent(null)
  }

  // Every opening starts with a clean form, and closing never sends anything.
  const close = () => {
    if (sending) return
    reset()
    onClose()
  }

  const trimmed = message.trim()
  const tooLong = trimmed.length > MAX_FEEDBACK_MESSAGE_LENGTH
  const messageError = touched && !trimmed ? 'Enter a response before sending.' : tooLong ? 'That response is too long.' : null

  const pick = async (file: File | undefined) => {
    // Cleared at once, so picking the same file again still fires a change.
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    // Fast feedback only: the server sniffs the bytes, decodes the image and checks both again.
    if (!(FEEDBACK_SCREENSHOT_MIME_TYPES as readonly string[]).includes(file.type) || file.size > FEEDBACK_SCREENSHOT_MAX_BYTES) {
      setScreenshotError(FEEDBACK_SCREENSHOT_RULE_TEXT)
      return
    }
    setScreenshotError(null)
    setReading(true)
    try {
      setScreenshot({ name: file.name, dataUrl: await readAsDataUrl(file) })
    } catch {
      setScreenshotError('Could not read that file.')
    } finally {
      setReading(false)
    }
  }

  const submit = async () => {
    setTouched(true)
    if (!trimmed || tooLong || reading || sending) return
    setSending(true)
    setNotSent(null)
    try {
      await sessionRepository().sendFeedback({
        pageKey,
        message: trimmed,
        ...(category ? { category } : {}),
        ...(screenshot ? { screenshot: screenshot.dataUrl } : {}),
      })
      reset()
      onSent()
    } catch (error) {
      // Kept as written, so it can be sent again.
      setNotSent(error instanceof RepositoryError ? error.message : 'Your feedback was not sent.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Feedback"
      onClose={close}
      onSubmit={() => void submit()}
      footer={
        <>
          <Button onClick={close} disabled={sending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={sending || reading}>
            {sending ? 'Sending…' : reading ? 'Reading screenshot…' : 'Submit feedback'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <p className={styles.question}>{question}</p>
        <Field label="Type" hint="Optional.">
          {(id) => (
            <Select id={id} className={styles.type} value={category} onChange={(event) => setCategory(event.target.value as '' | FeedbackCategory)}>
              <option value="">Not specified</option>
              {FEEDBACK_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {CATEGORY_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Your response"
          hint={`${trimmed.length}/${MAX_FEEDBACK_MESSAGE_LENGTH}. Please do not include passwords or other sensitive information.`}
          error={messageError}
        >
          {(id) => (
            <TextArea
              id={id}
              data-autofocus
              aria-required
              rows={6}
              value={message}
              maxLength={MAX_FEEDBACK_MESSAGE_LENGTH}
              aria-invalid={Boolean(messageError)}
              placeholder="What is working, unclear, or missing on this page?"
              onChange={(event) => setMessage(event.target.value)}
              onBlur={() => setTouched(true)}
            />
          )}
        </Field>
        <Field label="Screenshot" hint={`Optional. ${FEEDBACK_SCREENSHOT_RULE_TEXT} ${FEEDBACK_SCREENSHOT_WARNING}`} error={screenshotError}>
          {(id) => (
            <>
              <input
                ref={fileRef}
                id={id}
                type="file"
                hidden
                accept={FEEDBACK_SCREENSHOT_MIME_TYPES.join(',')}
                disabled={sending || reading}
                onChange={(event) => void pick(event.target.files?.[0])}
              />
              {screenshot ? (
                <div className={styles.attachment}>
                  {/* A local preview of the picked file; nothing is uploaded until Submit. */}
                  <img src={screenshot.dataUrl} alt="" className={styles.thumbnail} />
                  <span className={styles.fileName}>{screenshot.name}</span>
                  <Button disabled={sending || reading} onClick={() => setScreenshot(null)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <Button className={styles.attach} disabled={sending || reading} onClick={() => fileRef.current?.click()}>
                  {reading ? 'Reading screenshot…' : 'Attach a screenshot'}
                </Button>
              )}
            </>
          )}
        </Field>
        {/* Repeated once a file is attached: the warning is now about a file one click from being sent. */}
        {screenshot ? <div className={`${styles.notice} ${styles.warning}`}>Check this screenshot before sending. {FEEDBACK_SCREENSHOT_WARNING}</div> : null}
        {notSent ? (
          <div className={`${styles.notice} ${styles.error}`} role="alert">
            {notSent}
          </div>
        ) : null}
        <p className={styles.privacy}>
          We record the page you are on and your account, so we can follow up. Nothing else from the page is captured — only what
          you write and any screenshot you choose to attach.
        </p>
      </div>
    </Dialog>
  )
}
